import { useEffect, useRef } from "react";
import { syncOfflineTrips } from "@/lib/offline-trips";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n/useTranslation";
import { toast } from "sonner";

/**
 * Mounted once at the app root. Re-validates cached offline trips whenever
 * the device regains connectivity (or on initial load if already online).
 */
const OfflineSync = () => {
  const { user } = useAuth();
  const { t } = useT();
  const running = useRef(false);
  const lastRun = useRef(0);

  useEffect(() => {
    if (!user) return;

    /**
     * Silent background sync. We never surface a success toast (per UX
     * requirement) — the user only hears from us when something fails.
     * Conflicts are stored in localStorage and surfaced on the Offline page.
     */
    const run = async () => {
      if (running.current) return;
      if (Date.now() - lastRun.current < 15_000) return; // throttle
      running.current = true;
      try {
        await syncOfflineTrips();
        lastRun.current = Date.now();
      } catch {
        // Surface only failures — keep the message minimal & non-blocking.
        toast.error(t("offline.syncError") || "Sync hors-ligne en échec", { duration: 3000 });
      } finally {
        running.current = false;
      }
    };

    // Initial silent sync
    if (typeof navigator === "undefined" || navigator.onLine) {
      run();
    }

    const onOnline = () => run();
    const onVisible = () => { if (document.visibilityState === "visible") run(); };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, t]);

  return null;
};

export default OfflineSync;
